import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCommissionData } from '../../../Redux/campaignSlice';
import currencyData from '../../../data/currency.json';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CommissionPayouts = ({ onNext, onPrevious, data }) => {
  const dispatch = useDispatch();
  const campaignData = useSelector(state => state.campaign.campaignData);
  const [formData, setFormData] = useState(() => {
    // Initialize with passed data or default values
    const initialData = {
      commissionModel: 'fixed',
      payoutAmount: '',
      selectedCountry: '',
      percentage: '',
      cpe: '',
      cpm: ''
    };
    
    if (data) {
      // Map the data structure from Redux to form data
      return {
        commissionModel: data.model || 'fixed',
        payoutAmount: data.amount ? data.amount.toString() : '',
        selectedCountry: data.currency?.code || '',
        percentage: data.percentage ? data.percentage.toString() : '',
        cpe: data.cpe !== undefined && data.cpe !== null ? data.cpe.toString() : '',
        cpm: data.cpm !== undefined && data.cpm !== null ? data.cpm.toString() : ''
      };
    }
    
    return initialData;
  });
  const [countries] = useState(currencyData.countries);

  // Check if this is an advertising or hybrid campaign
  const isAdvertisingCampaign = campaignData?.general?.campaignType === 'Advertising';
  const isHybridCampaign = campaignData?.general?.campaignType === 'Hybrid';
  const isAffiliateCampaign = campaignData?.general?.campaignType === 'Affiliate';
  const isAdvertisingOrHybrid = isAdvertisingCampaign || isHybridCampaign;

  useEffect(() => {
    // If it's an advertising campaign, force commission model to 'fixed'
    if (isAdvertisingCampaign) {
      setFormData(prev => ({
        ...prev,
        commissionModel: 'fixed'
      }));
    }
  }, [isAdvertisingCampaign]);

  // Update form data when data prop changes (when coming back from previous steps)
  useEffect(() => {
    if (data) {
      setFormData({
        commissionModel: data.model || 'fixed',
        payoutAmount: data.amount ? data.amount.toString() : '',
        selectedCountry: data.currency?.code || '',
        percentage: data.percentage ? data.percentage.toString() : '',
        cpe: data.cpe !== undefined && data.cpe !== null ? data.cpe.toString() : '',
        cpm: data.cpm !== undefined && data.cpm !== null ? data.cpm.toString() : ''
      });
    }
  }, [data]);

  // Handle campaign type changes
  useEffect(() => {
    // If campaign type changes, reset form data appropriately
    if (isAdvertisingCampaign && formData.commissionModel !== 'fixed') {
      setFormData(prev => ({
        ...prev,
        commissionModel: 'fixed',
        percentage: '',
        cpe: '',
        cpm: ''
      }));
    }
  }, [isAdvertisingCampaign, formData.commissionModel]);

  const handleModelChange = (e) => {
    setFormData({
      ...formData,
      commissionModel: e.target.value,
      payoutAmount: '',
      percentage: '',
      cpe: '',
      cpm: ''
    });
  };

  const getCurrentCurrencySymbol = () => {
    const country = countries.find(c => c.code === formData.selectedCountry);
    return country ? country.symbol : '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.commissionModel || !formData.selectedCountry) {
      toast.error("Please fill in all required fields in Commission & Payouts section");
      return;
    }

    // Additional validation based on commission model
    if (formData.commissionModel === 'fixed' && !formData.payoutAmount) {
      toast.error("Please enter a payout amount");
      return;
    }

    if (formData.commissionModel === 'percentage' && !formData.percentage) {
      toast.error("Please enter a percentage value");
      return;
    }

    // Validate CPE and CPM for advertising and hybrid campaigns with fixed model
    if (isAdvertisingOrHybrid && formData.commissionModel === 'fixed') {
      if (formData.cpe === undefined || formData.cpe === null || formData.cpe === '') {
        toast.error("Please enter CPE (Cost Per Engagement)");
        return;
      }
      if (formData.cpm === undefined || formData.cpm === null || formData.cpm === '') {
        toast.error("Please enter CPM (Cost Per Thousand Views)");
        return;
      }
    }

    // Validate CPE and CPM for hybrid campaigns with percentage model
    if (isHybridCampaign && formData.commissionModel === 'percentage') {
      if (formData.cpe === undefined || formData.cpe === null || formData.cpe === '') {
        toast.error("Please enter CPE (Cost Per Engagement)");
        return;
      }
      if (formData.cpm === undefined || formData.cpm === null || formData.cpm === '') {
        toast.error("Please enter CPM (Cost Per Thousand Views)");
        return;
      }
    }

    // Create the commission data object with all required fields
    const commissionData = {
      model: formData.commissionModel,
      amount: formData.commissionModel === 'fixed' ? Number(formData.payoutAmount) : null,
      percentage: formData.commissionModel === 'percentage' ? Number(formData.percentage) : null,
      cpe: (isAdvertisingOrHybrid && formData.commissionModel === 'fixed') || 
           (isHybridCampaign && formData.commissionModel === 'percentage') ? Number(formData.cpe) : null,
      cpm: (isAdvertisingOrHybrid && formData.commissionModel === 'fixed') || 
           (isHybridCampaign && formData.commissionModel === 'percentage') ? Number(formData.cpm) : null,
      currency: {
        code: formData.selectedCountry,
        symbol: getCurrentCurrencySymbol()
      }
    };

    // Dispatch to Redux first
    dispatch(setCommissionData(commissionData));
    
    // Then call onNext with the data
    onNext(commissionData);
  };

  return (
    <div className="w-full bg-white shadow p-4 md:p-6 rounded-md">
      <h3 className="text-lg font-semibold mb-4">Commission & Payouts</h3>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Country Selection */}
        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700">
            Currency <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.selectedCountry}
            onChange={(e) => setFormData({ ...formData, selectedCountry: e.target.value })}
            className="w-full border border-orange-300 rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
            required
          >
            <option value="">Select Currency</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name} ({country.currency})
              </option>
            ))}
          </select>
        </div>

        {/* Commission Model Selection */}
        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700">
            Commission Model <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.commissionModel}
            onChange={handleModelChange}
            className="w-full border border-orange-300 rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
            disabled={isAdvertisingCampaign}
          >
            <option value="fixed">Fixed Amount</option>
            <option value="percentage" disabled={isAdvertisingCampaign}>Percentage</option>
          </select>
          {isAdvertisingCampaign && (
            <p className="mt-1 text-sm text-gray-500">Percentage model is not available for advertising campaigns</p>
          )}
          {isHybridCampaign && (
            <p className="mt-1 text-sm text-gray-500">Hybrid campaigns support both fixed and percentage models with advertising metrics</p>
          )}
          {isAffiliateCampaign && (
            <p className="mt-1 text-sm text-gray-500">Affiliate campaigns support both fixed and percentage models</p>
          )}
        </div>
        
        {/* Dynamic Payout Fields based on Commission Model and Campaign Type */}
        {formData.commissionModel === 'fixed' && (
          <>
            {/* Fixed Amount */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">
                Fixed Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">{getCurrentCurrencySymbol()}</span>
                </div>
                <input
                 onWheel={(e) => e.target.blur()}
                  type="number"
                  value={formData.payoutAmount}
                  onChange={(e) => setFormData({ ...formData, payoutAmount: e.target.value })}
                  className="w-full border border-orange-300 rounded-md p-2 pl-7 focus:border-orange-500 focus:ring-orange-500"
                  placeholder="Enter fixed amount"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>

            {/* CPE and CPM for Advertising and Hybrid Campaigns */}
            {isAdvertisingOrHybrid && formData.commissionModel === 'fixed' && (
              <>
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700">
                    CPE (Cost Per Engagement) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">{getCurrentCurrencySymbol()}</span>
                    </div>
                    <input
                      onWheel={(e) => e.target.blur()}
                      type="number"
                      value={formData.cpe}
                      onChange={(e) => setFormData({ ...formData, cpe: e.target.value })}
                      className="w-full border border-orange-300 rounded-md p-2 pl-7 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Enter CPE"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Cost per engagement for advertising metrics
                  </p>
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700">
                    CPM (Cost Per Thousand Views) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">{getCurrentCurrencySymbol()}</span>
                    </div>
                    <input
                      onWheel={(e) => e.target.blur()}
                      type="number"
                      value={formData.cpm}
                      onChange={(e) => setFormData({ ...formData, cpm: e.target.value })}
                      className="w-full border border-orange-300 rounded-md p-2 pl-7 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Enter CPM"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Cost per thousand views for advertising metrics
                  </p>
                </div>
              </>
            )}
          </>
        )}

        {formData.commissionModel === 'percentage' && !isAdvertisingCampaign && (
          <>
            {/* Percentage Field */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">
                Percentage <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
                <input
                  onWheel={(e) => e.target.blur()}
                  type="number"
                  value={formData.percentage}
                  onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                  className="w-full border border-orange-300 rounded-md p-2 pr-8 focus:border-orange-500 focus:ring-orange-500"
                  placeholder="Enter percentage"
                  min="0"
                  max="100"
                  step="0.01"
                  required
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">%</span>
                </div>
              </div>
              <p className="mt-1 text-sm text-gray-500">Enter a value between 0 and 100</p>
            </div>

            {/* CPE and CPM for Hybrid Campaigns with Percentage Model */}
            {isHybridCampaign && formData.commissionModel === 'percentage' && (
              <>
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700">
                    CPE (Cost Per Engagement) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">{getCurrentCurrencySymbol()}</span>
                    </div>
                    <input
                      onWheel={(e) => e.target.blur()}
                      type="number"
                      value={formData.cpe}
                      onChange={(e) => setFormData({ ...formData, cpe: e.target.value })}
                      className="w-full border border-orange-300 rounded-md p-2 pl-7 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Enter CPE"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Cost per engagement for advertising metrics
                  </p>
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700">
                    CPM (Cost Per Thousand Views) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">{getCurrentCurrencySymbol()}</span>
                    </div>
                    <input
                      onWheel={(e) => e.target.blur()}
                      type="number"
                      value={formData.cpm}
                      onChange={(e) => setFormData({ ...formData, cpm: e.target.value })}
                      className="w-full border border-orange-300 rounded-md p-2 pl-7 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Enter CPM"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Cost per thousand views for advertising metrics
                  </p>
                </div>
              </>
            )}
          </>
        )}

        {/* Preview Section */}
        {formData.selectedCountry && (
          <div className="w-full bg-gray-50 rounded-md p-3">
            <h4 className="text-sm font-medium text-gray-700">Preview</h4>
            <p className="text-sm text-gray-600">
              {formData.commissionModel === 'fixed' ? (
                <>
                  <div>Fixed payout: {getCurrentCurrencySymbol()}{formData.payoutAmount}</div>
                  {isAdvertisingOrHybrid && formData.cpe && formData.cpm && (
                    <>
                      <div>CPE: {getCurrentCurrencySymbol()}{formData.cpe}</div>
                      <div>CPM: {getCurrentCurrencySymbol()}{formData.cpm}</div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div>Commission: {formData.percentage}% of sale</div>
                  {isHybridCampaign && formData.cpe && formData.cpm && (
                    <>
                      <div>CPE: {getCurrentCurrencySymbol()}{formData.cpe}</div>
                      <div>CPM: {getCurrentCurrencySymbol()}{formData.cpm}</div>
                    </>
                  )}
                </>
              )}
            </p>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={onPrevious}
            className="bg-gray-300 px-4 py-2 rounded-md"
          >
            Previous
          </button>
          <button
            type="submit"
            className="bg-orange-500 text-white px-4 py-2 rounded-md"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default CommissionPayouts; 














// import React, { useState } from 'react';

// const CommissionPayouts = () => {
//   const [commissionModel, setCommissionModel] = useState('fixed');
//   const [payoutAmount, setPayoutAmount] = useState('');
//   const [selectedCountry, setSelectedCountry] = useState('');
//   const [percentage, setPercentage] = useState('');

//   // Sample country-currency mapping (you can expand this list)
//   const countryCurrencyMap = [
//     { country: 'United States', code: 'US', currency: 'USD', symbol: '$' },
//     { country: 'India', code: 'IN', currency: 'INR', symbol: '₹' },
//     { country: 'United Kingdom', code: 'GB', currency: 'GBP', symbol: '£' },
//     { country: 'European Union', code: 'EU', currency: 'EUR', symbol: '€' },
//     { country: 'Japan', code: 'JP', currency: 'JPY', symbol: '¥' },
//   ];

//   const handleModelChange = (e) => {
//     setCommissionModel(e.target.value);
//     // Reset values when switching models
//     setPayoutAmount('');
//     setPercentage('');
//   };

//   const handleCountryChange = (e) => {
//     setSelectedCountry(e.target.value);
//   };

//   const getCurrentCurrencySymbol = () => {
//     const country = countryCurrencyMap.find(c => c.code === selectedCountry);
//     return country ? country.symbol : '';
//   };

//   return (
//     <div className="w-full bg-white shadow p-4 md:p-6 rounded-md">
//       <h3 className="text-lg font-semibold mb-4">Commission & Payouts</h3>
//       <form className="space-y-5">
//         {/* Country Selection */}
//         <div className="w-full">
//           <label className="block text-sm font-medium text-gray-700">
//             Country <span className="text-red-500">*</span>
//           </label>
//           <select
//             value={selectedCountry}
//             onChange={handleCountryChange}
//             className="w-full border border-orange-300 rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
//           >
//             <option value="">Select Country</option>
//             {countryCurrencyMap.map((country) => (
//               <option key={country.code} value={country.code}>
//                 {country.country} ({country.currency})
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Commission Model Selection */}
//         <div className="w-full">
//           <label className="block text-sm font-medium text-gray-700">
//             Commission Model <span className="text-red-500">*</span>
//           </label>
//           <select
//             value={commissionModel}
//             onChange={handleModelChange}
//             className="w-full border border-orange-300 rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500"
//           >
//             <option value="fixed">Fixed Amount</option>
//             <option value="percentage">Percentage</option>
//           </select>
//         </div>
        
//         {/* Dynamic Payout Field based on Commission Model */}
//         <div className="w-full">
//           <label className="block text-sm font-medium text-gray-700">
//             {commissionModel === 'fixed' ? 'Fixed Amount' : 'Percentage'} <span className="text-red-500">*</span>
//           </label>
//           <div className="relative mt-1">
//             {commissionModel === 'fixed' ? (
//               <>
//                 <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//                   <span className="text-gray-500">{getCurrentCurrencySymbol()}</span>
//                 </div>
//                 <input
//                   type="number"
//                   value={payoutAmount}
//                   onChange={(e) => setPayoutAmount(e.target.value)}
//                   className="w-full border border-orange-300 rounded-md p-2 pl-7 focus:border-orange-500 focus:ring-orange-500"
//                   placeholder="Enter fixed amount"
//                   min="0"
//                   step="0.01"
//                 />
//               </>
//             ) : (
//               <>
//                 <input
//                   type="number"
//                   value={percentage}
//                   onChange={(e) => setPercentage(e.target.value)}
//                   className="w-full border border-orange-300 rounded-md p-2 pr-8 focus:border-orange-500 focus:ring-orange-500"
//                   placeholder="Enter percentage"
//                   min="0"
//                   max="100"
//                   step="0.01"
//                 />
//                 <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
//                   <span className="text-gray-500">%</span>
//                 </div>
//               </>
//             )}
//           </div>
//           {commissionModel === 'percentage' && (
//             <p className="mt-1 text-sm text-gray-500">Enter a value between 0 and 100</p>
//           )}
//         </div>

//         {/* Preview Section */}
//         {selectedCountry && (payoutAmount || percentage) && (
//           <div className="w-full  bg-gray-50 rounded-md">
//             <h4 className="text-sm font-medium text-gray-700 ">Preview</h4>
//             <p className="text-sm text-gray-600">
//               {commissionModel === 'fixed' 
//                 ? `Fixed payout: ${getCurrentCurrencySymbol()}${payoutAmount}`
//                 : `Commission: ${percentage}% of sale`
//               }
//             </p>
//           </div>
//         )}

//         {/* Submit Button */}
//         <div className="text-right">
//           <button
//             type="submit"
//             className="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 transition duration-300"
//           >
//             Save
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default CommissionPayouts; 


